import { useEffect, useState } from "react";
import {
  listMfaFactors,
  enrollMfaTotp,
  challengeMfa,
  verifyMfa,
  unenrollMfa,
  type MfaEnrollResult,
} from "@/hooks/useMfa";

/**
 * İki faktörlü doğrulama (2FA / TOTP) kurulumu.
 * Kullanıcı burada uygulama (Google Authenticator vb.) ile QR kodunu eşleştirir
 * ve 6 haneli kodu doğrulayarak 2FA'yı aktifleştirir.
 */
export default function TwoFactorSetup() {
  const [hasTotp, setHasTotp] = useState(false);
  const [checking, setChecking] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [enroll, setEnroll] = useState<MfaEnrollResult | null>(null);
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { totp } = await listMfaFactors();
        if (active) setHasTotp(totp.length > 0);
      } catch {
        if (active) setHasTotp(false);
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const startEnroll = async () => {
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      const result = await enrollMfaTotp("DersDinle");
      const cid = await challengeMfa(result.id);
      setEnroll(result);
      setChallengeId(cid);
      setCode("");
      setEnrolling(true);
    } catch {
      setError("2FA kurulumu başlatılamadı. Lütfen tekrar deneyin.");
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async () => {
    if (!enroll || !challengeId) return;
    setError(null);
    setBusy(true);
    try {
      await verifyMfa(enroll.id, challengeId, code.trim());
      setHasTotp(true);
      setEnrolling(false);
      setEnroll(null);
      setMessage("İki faktörlü doğrulama başarıyla aktifleştirildi.");
    } catch {
      setError("Kod doğrulanamadı. Lütfen doğru kodu girdiğinden emin ol.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      const { totp } = await listMfaFactors();
      for (const f of totp) {
        await unenrollMfa(f.id);
      }
      setHasTotp(false);
      setMessage("İki faktörlü doğrulama kapatıldı.");
    } catch {
      setError("2FA kapatılırken bir hata oluştu.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-6">
      <p className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
        <i className="ri-shield-check-line text-primary-600" aria-hidden="true"></i>
        İki Faktörlü Doğrulama (2FA)
      </p>
      <p className="mt-1 text-sm text-foreground-600 leading-relaxed">
        Hesabını ekstra bir güvenlik katmanıyla koru. Google Authenticator gibi bir
        uygulamayla 6 haneli kodu kullanarak giriş yap.
      </p>

      {checking ? (
        <p className="mt-4 text-sm text-foreground-500">Kontrol ediliyor...</p>
      ) : enrolling && enroll ? (
        <div className="mt-4 space-y-4">
          <div className="flex items-start gap-4">
            {enroll.qrCode && (
              <img src={enroll.qrCode} alt="2FA QR kodu" className="w-32 h-32 rounded-lg border border-background-200 shrink-0" />
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground-900">1. Uygulamayla tara</p>
              <p className="mt-1 text-xs text-foreground-600 leading-relaxed">
                Google Authenticator (veya benzeri) uygulamasıyla QR kodu tara.
              </p>
              <p className="mt-3 text-sm font-medium text-foreground-900">2. Kodu gir</p>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                className="mt-1.5 w-40 h-10 px-3 rounded-md border border-background-200 text-sm tracking-[0.4em] text-center font-semibold text-foreground-900 focus:outline-none focus:border-primary-400"
              />
              <p className="mt-2 text-xs text-foreground-400 break-all">Manuel anahtar: {enroll.secret}</p>
            </div>
          </div>
          {error && <p className="text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">{error}</p>}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={confirmEnroll}
              disabled={busy || code.length !== 6}
              className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
            >
              {busy ? "Doğrulanıyor..." : "Doğrula ve Aktifleştir"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEnrolling(false);
                setEnroll(null);
              }}
              className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Vazgeç
            </button>
          </div>
        </div>
      ) : hasTotp ? (
        <div className="mt-4">
          <div className="flex items-center gap-2 rounded-md bg-accent-100/70 px-4 py-3">
            <i className="ri-checkbox-circle-line text-accent-700" aria-hidden="true"></i>
            <span className="text-sm font-medium text-accent-900">İki faktörlü doğrulama aktif.</span>
          </div>
          <button
            type="button"
            onClick={disable}
            disabled={busy}
            className="mt-3 h-10 px-4 rounded-md text-sm font-medium text-red-700 hover:bg-red-50 transition-colors whitespace-nowrap"
          >
            {busy ? "Kapatılıyor..." : "2FA'yı Kapat"}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={startEnroll}
          disabled={busy}
          className="mt-4 h-10 px-4 rounded-md bg-secondary-500 text-background-50 text-sm font-semibold hover:bg-secondary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
        >
          {busy ? "Hazırlanıyor..." : "2FA'yı Etkinleştir"}
        </button>
      )}

      {message && (
        <p className="mt-3 text-sm text-accent-800 bg-accent-100/60 rounded-md px-3 py-2">{message}</p>
      )}
    </div>
  );
}